
import React from 'react';
import { useCart } from '../context/CartContext';
import { CloseIcon, TrashIcon } from '../assets/icons'; // Assuming these exist, otherwise I'll define simple SVGs or verify icons
import { Page } from '../types';

interface CartDrawerProps {
    onNavigate: (page: Page) => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ onNavigate }) => {
    const { cartItems, isCartOpen, toggleCart, removeFromCart, updateQuantity, totalAmount } = useCart();

    if (!isCartOpen) return null;

    const handleCheckout = () => {
        toggleCart();
        onNavigate('checkout' as Page); // We'll need to add 'checkout' to Page type
    };

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black/70 transition-opacity" onClick={toggleCart}></div>

            <div className="fixed inset-y-0 right-0 max-w-full flex">
                <div className="w-screen max-w-md pointer-events-auto">
                    <div className="h-full flex flex-col bg-gray-900 border-l border-gray-800 shadow-2xl">

                        {/* Header */}
                        <div className="px-6 py-6 border-b border-gray-800 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-white">Seu carrinho</h2>
                            <button onClick={toggleCart} className="text-gray-400 hover:text-white transition-colors">
                                <CloseIcon className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto px-6 py-4">
                            {cartItems.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center text-gray-400">
                                    <svg className="w-16 h-16 mb-4 opacity-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    <p className="text-lg">Seu carrinho está vazio.</p>
                                    <button onClick={toggleCart} className="mt-6 text-arsenal-red hover:underline font-bold">
                                        Continuar Comprando
                                    </button>
                                </div>
                            ) : (
                                <ul className="space-y-6">
                                    {cartItems.map((item) => (
                                        <li key={item.id} className="flex gap-4 group">
                                            <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0 border border-gray-700">
                                                <img src={item.imageUrls[0]} alt={item.name} className="w-full h-full object-cover" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex justify-between items-start">
                                                    <h3 className="text-white font-bold text-sm line-clamp-2">{item.name}</h3>
                                                    <button onClick={() => removeFromCart(item.id)} className="text-gray-500 hover:text-red-500 transition-colors p-1">
                                                        <TrashIcon className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                <p className="text-xs text-gray-400 mt-1">{item.category} • {item.caliber}</p>
                                                <div className="flex justify-between items-end mt-3">
                                                    <div className="flex items-center border border-gray-700 rounded-lg bg-gray-800">
                                                        <button
                                                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                            className="px-2 py-1 text-gray-400 hover:text-white transition-colors"
                                                        >-</button>
                                                        <span className="text-xs font-bold text-white px-1 w-4 text-center">{item.quantity}</span>
                                                        <button
                                                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                                            className="px-2 py-1 text-gray-400 hover:text-white transition-colors"
                                                        >+</button>
                                                    </div>
                                                    <span className="font-bold text-arsenal-red">{item.price}</span>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Footer */}
                        {cartItems.length > 0 && (
                            <div className="px-6 py-6 border-t border-gray-800 bg-gray-900">
                                <div className="flex justify-between items-center mb-4">
                                    <span className="text-gray-400">Subtotal</span>
                                    <span className="text-2xl font-black text-white">
                                        R$ {totalAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-500 mb-6 text-center">
                                    Taxas e frete calculados no checkout. Sujeito a verificação de CR/CRAF.
                                </p>
                                <button
                                    onClick={handleCheckout}
                                    className="w-full bg-arsenal-red hover:bg-red-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-red-900/30 transition-all hover:-translate-y-1"
                                >
                                    Solicitar Orçamento / Reservar
                                </button>
                                <button onClick={toggleCart} className="w-full mt-3 text-gray-400 hover:text-white text-sm font-medium">
                                    Continuar Comprando
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartDrawer;
